/* Import d'un examen sur table — trois cas dans une seule modale :
   1. le sujet seul (éventuellement à plusieurs variantes anti-triche) ;
   2. le sujet ET son corrigé ;
   3. le corrigé d'un examen déjà importé.
   Un nouvel import du corrigé COMPLÈTE l'examen (upsert côté serveur sur la
   variante 'answer_key') : jamais de doublon, le sujet reste en place.

   Spec 13 : au clic Importer (mode 'new'), la structuration démarre en
   arrière-plan (runExamImportStructureJob, même mécanisme que
   bootstrap_course) — la modale bascule en `phase: 'structuring'`. Le
   formulaire de saisie ne change pas de nature, seul l'état qui suit le clic
   est nouveau.

   NAI-218 (retour Yassine, 01/10) : les phases 'uploading'/'structuring' ne
   rendent plus d'overlay bloquant — ce composant reste monté (le suivi/
   chaînage du job continue de tourner, voir chainAnswerKeyRef plus bas) mais
   n'affiche rien, laissant ExamImportProgressBar + ExamImportNoticeToast
   (exam-import-progress.tsx, montés globalement dans AppShell) prendre le
   relais visuellement. Ceci remplace l'ancien loader plein écran "SANS
   Annuler (décision explicite d'Ismaël, spec 13)" : la même décision du
   30/09 qui a introduit le mécanisme de job de fond rend cet écran bloquant
   obsolète — le job continue déjà indépendamment de la modale, il n'y a plus
   de raison de retenir l'utilisateur dessus. */

import { useEffect, useRef, useState, type ChangeEvent, type RefObject } from 'react';
import { Images, Paperclip, Plus, WarningCircle, X } from '@phosphor-icons/react';
import { api } from '../lib/api';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import AuthoringWarningsList, { type AuthoringWarningRow } from './authoring-warnings';
import { useExamImportJobs, useTrackedExamImportJob } from '../hooks/use-exam-import-jobs';
import { examImportFailureMessage } from './exam-import-progress';

interface Classroom {
  id: string;
  name: string;
  subjects?: { id: string; label: string | null }[];
}
interface ExamDoc {
  id: string;
  title: string;
  archived_at: string | null;
}
interface Props {
  onClose: () => void;
  onImported: (documentId: string) => void;
}

type Mode = 'new' | 'answer_key';
/** État après le clic Importer (mode 'new' uniquement) — voir spec 13.
 *  'warnings' : le job a réussi mais a signalé des points à vérifier
 *  (visuel non extrait, conflit de barème…) — jamais un succès muet, voir
 *  authoring-warnings.tsx. */
type Phase = 'form' | 'uploading' | 'structuring' | 'warnings' | 'error';

const extOf = (f: File) => (f.name.split('.').pop() ?? 'pdf').toLowerCase();
const mimeOf = (f: File) => f.type || 'application/pdf';
const sizeMo = (f: File) => (f.size / 1024 / 1024).toFixed(1);

/* Le sujet comme le corrigé peuvent être une photo ou une capture d'écran :
   le contrat tRPC porte déjà le `mimeType` et le moteur lit les images. */
const ACCEPT = 'application/pdf,image/jpeg,image/png,image/heic,image/webp';

/* <option> n'accepte que background-color/color en style inline — tout le
   reste de la boîte de dialogue du navigateur reste hors de portée du CSS. */
const optionStyle = { backgroundColor: 'var(--naia-card)', color: 'var(--naia-text-primary)' };

/* Un `fetch` qui n'aboutit pas (réseau coupé, requête bloquée) rejette avec un
   TypeError dont le message navigateur (« Failed to fetch ») ne dit rien à
   l'enseignant : on le traduit par une consigne actionnable. */
const errorMessage = (e: unknown) =>
  e instanceof TypeError
    ? "L'envoi n'a pas abouti — vérifiez votre connexion, puis réessayez."
    : e instanceof Error
      ? e.message
      : String(e);

const modeClass = (active: boolean) =>
  `flex-1 rounded-sm border-[1.5px] px-3 py-2 text-[13px] font-bold cursor-pointer transition-colors ${
    active
      ? 'border-naia-blue bg-naia-sky text-naia-blue'
      : 'border-border bg-card text-[var(--naia-text-secondary)] hover:border-naia-blue'
  }`;

export default function ExamImportModal({ onClose, onImported }: Props) {
  const [mode, setMode] = useState<Mode>('new');
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [exams, setExams] = useState<ExamDoc[]>([]);
  const [classroomId, setClassroomId] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [note, setNote] = useState('');
  const [subject, setSubject] = useState<File | null>(null);
  const [answerKey, setAnswerKey] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const answerKeyRef = useRef<HTMLInputElement>(null);

  /* Case "Le sujet a des variantes ?" — noms saisis AVANT import, jamais
     devinés par le job (spec 13). Décochée par défaut : formulaire inchangé. */
  const [hasVariants, setHasVariants] = useState(false);
  const [variantLabels, setVariantLabels] = useState<string[]>(['', '']);

  const [phase, setPhase] = useState<Phase>('form');
  const [jobWarnings, setJobWarnings] = useState<readonly AuthoringWarningRow[]>([]);
  const [structuredDocId, setStructuredDocId] = useState<string | null>(null);
  /** Quel job de fond est en cours/à réessayer — structuration initiale ou
   *  complétion d'un corrigé importé après coup (spec 13). */
  const [structuringKind, setStructuringKind] = useState<'new' | 'answer_key'>('new');
  /* Sujet + corrigé fournis ENSEMBLE à la création, sur un examen à
   * variantes : le chat classique n'écrit que la référence (spec 13), donc
   * le corrigé doit passer par le même job que l'import dédié — enchaîné
   * automatiquement dès que la structuration réussit, jamais laissé au
   * teacher de le redemander séparément (retour Ismaël, staging, lot 3 :
   * un answer_key.pdf fourni à la création restait sinon totalement ignoré,
   * aucun job ne le traitant jamais).
   *
   * En ref, PAS en state : un state ici est lu par le useEffect de sondage
   * ci-dessous, qui redémarre (nouvel intervalle, nouveau sondage immédiat)
   * dès que ce state change — AVANT que le `await startAnswerKey.mutate(...)`
   * qui crée le job de complétion soit résolu. Ce sondage prématuré relit
   * alors encore le job de structuration (le seul qui existe à cet instant),
   * mais avec la garde de chaînage déjà désactivée : il traite ses
   * avertissements comme définitifs et affiche l'écran "à vérifier" avant
   * même que le job de complétion ait eu la chance de les rendre obsolètes
   * (retour Ismaël, staging, lot 7 — capturé avec preuve en base : le job de
   * complétion tourne bien et corrige tout ~1 minute plus tard, mais l'écran
   * affiché au prof ne se remet jamais à jour une fois en phase 'warnings'). */
  const chainAnswerKeyRef = useRef(false);
  /* Deuxième garde, indépendante de la précédente : l'id du job de
   * structuration au moment où on décide de chaîner. Tant que le sondage
   * revoit CE MÊME id (le job de complétion pas encore créé, ou pas encore
   * "le plus récent"), on ignore purement son statut — identité de job,
   * jamais un state React dont le timing de mise à jour n'est pas garanti
   * synchrone avec l'appel réseau qui crée le nouveau job. */
  const chainedFromJobIdRef = useRef<string | null>(null);

  /* Mode 'answer_key' — variantes du document ciblé, pour savoir si le
     corrigé doit passer par le job de fond (>=2 variantes, le chat classique
     n'écrit que sur la référence) ou par le chat comme aujourd'hui (spec 13). */
  const [targetVariantCount, setTargetVariantCount] = useState(0);
  useEffect(() => {
    if (mode !== 'answer_key' || !documentId) { setTargetVariantCount(0); return; }
    let cancelled = false;
    api.content.get.query({ id: documentId })
      .then((d) => { if (!cancelled) setTargetVariantCount((d as { variants?: unknown[] } | null)?.variants?.length ?? 0); })
      .catch(() => { if (!cancelled) setTargetVariantCount(0); });
    return () => { cancelled = true; };
  }, [mode, documentId]);

  useEffect(() => {
    api.teacher.myClassrooms
      .query()
      .then((rows) => setClassrooms(rows as Classroom[]))
      .catch(() => {});
    api.content.list
      .query({ kind: 'exam', includeArchived: false, limit: 100 })
      .then((r) => setExams((r as { items: ExamDoc[] }).items.filter((d) => !d.archived_at)))
      .catch(() => {});
  }, []);

  /* Suivi du job de structuration tant que la modale est en phase
     'structuring' — sourcé sur le contexte global (barre de progression),
     plus de polling local dupliqué (voir hooks/use-exam-import-jobs.tsx). */
  const { trackJob, releaseJob } = useExamImportJobs();
  const trackedJob = useTrackedExamImportJob(structuredDocId);

  useEffect(() => {
    if (phase !== 'structuring' || !structuredDocId) return;
    trackJob(structuredDocId, { claim: true });
  }, [phase, structuredDocId, trackJob]);

  /* La modale rend le job au contexte global à sa fermeture/démontage : s'il
     tourne encore, la barre + le toast globaux en reprennent le suivi ; s'il
     est déjà terminal, cette modale l'a déjà montré, pas de notification. */
  useEffect(() => {
    if (!structuredDocId) return;
    return () => releaseJob(structuredDocId);
  }, [structuredDocId, releaseJob]);

  useEffect(() => {
    if (phase !== 'structuring' || !structuredDocId || !trackedJob) return;
    if (chainedFromJobIdRef.current && trackedJob.id === chainedFromJobIdRef.current) {
      /* Toujours le job de structuration périmé — le job de complétion
         n'existe pas encore (ou n'est pas encore "le plus récent"). On
         ignore purement son statut/ses avertissements et on continue de
         sonder, plutôt que de les traiter comme définitifs. */
      return;
    }
    if (trackedJob.status === 'succeeded') {
      if (chainAnswerKeyRef.current) {
        /* La structuration du sujet vient de réussir et un corrigé a été
           fourni en même temps sur un examen à variantes — enchaîne sur
           le job de complétion avant de considérer l'import terminé. */
        chainAnswerKeyRef.current = false;
        chainedFromJobIdRef.current = trackedJob.id;
        setStructuringKind('answer_key');
        void (async () => {
          try {
            await api.content.examImport.startAnswerKey.mutate({ documentId: structuredDocId });
            /* Repolle immédiatement le contexte plutôt que d'attendre le
               prochain tick d'intervalle (jusqu'à POLL_MS) : sans ça, le job
               de complétion tout juste créé n'est détecté qu'à la prochaine
               sondage périodique, un délai inutile côté UX comme côté tests. */
            trackJob(structuredDocId, { claim: true });
          } catch (e) {
            setError(errorMessage(e));
            setPhase('error');
          }
        })();
        return;
      }
      if (trackedJob.warnings && trackedJob.warnings.length > 0) {
        setJobWarnings(trackedJob.warnings as readonly AuthoringWarningRow[]);
        setPhase('warnings');
      } else {
        onImported(structuredDocId);
      }
    } else if (trackedJob.status === 'failed') {
      /* Jamais trackedJob.error brut (détails Vertex : modèle, région, code
         HTTP — repro bêta-testeur 16/10) — toujours un message FR actionnable
         dérivé du failureKind, partagé avec exam-import-progress.tsx. */
      setError(examImportFailureMessage(trackedJob.failureKind));
      setPhase('error');
    }
  /* structuringKind est délibérément ABSENT de ce tableau : la lecture repose
     désormais sur chainAnswerKeyRef/chainedFromJobIdRef (des refs, pas des
     deps réactives) précisément pour que ce useEffect ne redémarre PAS au
     milieu d'une chaîne structuration→complétion (voir le commentaire sur
     chainAnswerKeyRef ci-dessus — c'est ce redémarrage qui causait la
     course). */
  }, [phase, structuredDocId, trackedJob, onImported, trackJob]);

  const pick = (setter: (f: File | null) => void) => (e: ChangeEvent<HTMLInputElement>) => {
    setter(e.target.files?.[0] ?? null);
    e.target.value = '';
    setError(null);
  };

  const put = async (upload: { path: string; signedUrl: string }, file: File) => {
    const res = await fetch(upload.signedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': mimeOf(file) },
      body: file,
    });
    if (!res.ok) throw new Error(`Échec de l'envoi de « ${file.name} » (${res.status})`);
  };

  const validVariantLabels = variantLabels.map((l) => l.trim()).filter(Boolean);
  const canSubmit =
    mode === 'new'
      ? Boolean(subject && classroomId) && (!hasVariants || validVariantLabels.length >= 2)
      : Boolean(answerKey && documentId);

  const submit = async () => {
    if (!canSubmit || phase !== 'form') return;
    setPhase('uploading');
    setError(null);
    try {
      if (mode === 'new') {
        const result = await api.content.createFromModal.mutate({
          kind: 'exam',
          classroomIds: [classroomId],
          note: note.trim(),
          attachment: subject ? { ext: extOf(subject), mimeType: mimeOf(subject) } : undefined,
          answerKey: answerKey ? { ext: extOf(answerKey), mimeType: mimeOf(answerKey) } : undefined,
          variantLabels: hasVariants ? validVariantLabels : undefined,
        });
        if (subject && result.upload) await put(result.upload, subject);
        if (answerKey && result.answerKeyUpload) await put(result.answerKeyUpload, answerKey);

        setStructuredDocId(result.document.id);
        setStructuringKind('new');
        chainedFromJobIdRef.current = null;
        /* Sujet + corrigé fournis ensemble sur un examen à variantes : le
           corrigé ne peut couvrir les variantes non-référence que via le job
           dédié (spec 13), jamais via le chat classique — enchaîné dès que
           la structuration réussit (cf. effet de sondage ci-dessous). */
        chainAnswerKeyRef.current = Boolean(answerKey) && hasVariants && validVariantLabels.length >= 2;
        setPhase('structuring');
        await api.content.examImport.start.mutate({ documentId: result.document.id });
      } else {
        const upload = await api.content.importAnswerKey.mutate({
          documentId,
          attachment: { ext: extOf(answerKey!), mimeType: mimeOf(answerKey!) },
        });
        await put({ path: upload.path, signedUrl: upload.signed_url }, answerKey!);
        if (targetVariantCount >= 2) {
          /* Le chat classique n'écrit que sur la variante de référence — un
             examen à variantes passe par le même job de fond que l'import
             initial (spec 13). */
          setStructuredDocId(documentId);
          setStructuringKind('answer_key');
          setPhase('structuring');
          await api.content.examImport.startAnswerKey.mutate({ documentId });
        } else {
          onImported(documentId);
        }
      }
    } catch (e) {
      setError(errorMessage(e));
      setPhase('error');
    }
  };

  const retry = async () => {
    if (!structuredDocId) return;
    setError(null);
    setPhase('structuring');
    try {
      if (structuringKind === 'answer_key') {
        await api.content.examImport.startAnswerKey.mutate({ documentId: structuredDocId });
      } else {
        await api.content.examImport.start.mutate({ documentId: structuredDocId });
      }
    } catch (e) {
      setError(errorMessage(e));
      setPhase('error');
    }
  };

  const fileRow = (label: string, file: File | null, inputRef: RefObject<HTMLInputElement | null>, onPick: (e: ChangeEvent<HTMLInputElement>) => void, required: boolean) => (
    <div className="flex flex-col gap-[6px] text-[13px] font-bold text-muted-foreground">
      <span>
        {label}
        {required ? '' : ' (optionnel)'}
      </span>
      <input ref={inputRef} type="file" accept={ACCEPT} onChange={onPick} className="sr-only" />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="flex items-center gap-2 self-start rounded-full border border-border bg-card px-3 py-2 text-[12.5px] font-semibold text-naia-blue hover:bg-naia-sky"
      >
        <Paperclip size={14} weight="bold" />
        {file ? 'Changer le fichier' : 'Choisir un PDF ou une photo'}
      </button>
      {file && (
        <span className="font-normal text-[12.5px] text-[var(--naia-text-secondary)]">
          {file.name} · {sizeMo(file)} Mo
        </span>
      )}
    </div>
  );

  /* Non-bloquant (NAI-218) — rien à l'écran ici : le composant reste monté
     pour que le suivi/chaînage du job continue (effets ci-dessus), mais
     l'utilisateur voit la barre + le toast globaux, pas cette modale. */
  if (phase === 'structuring' || phase === 'uploading') return null;

  if (phase === 'warnings') {
    return (
      <div className="fixed inset-0 bg-black/45 flex items-center justify-center z-50">
        <div className="bg-card w-full max-w-[460px] mx-4 rounded-[20px] p-6 flex flex-col gap-4">
          <h2 className="m-0 text-[15px] font-bold">Import terminé — à vérifier</h2>
          <AuthoringWarningsList warnings={jobWarnings} />
          <div className="flex justify-end gap-[10px]">
            <Button variant="secondary" onClick={onClose}>Fermer</Button>
            <Button onClick={() => structuredDocId && onImported(structuredDocId)}>Continuer</Button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="fixed inset-0 bg-black/45 flex items-center justify-center z-50">
        <div className="bg-card w-full max-w-[420px] mx-4 rounded-[20px] p-6 flex flex-col gap-4">
          <div className="flex items-start gap-2">
            <WarningCircle size={20} className="shrink-0 text-[var(--naia-error,#dc2626)]" weight="fill" />
            <p className="m-0 text-[13.5px] leading-snug">{error ?? 'La structuration a échoué.'}</p>
          </div>
          <div className="flex gap-[10px] justify-end flex-wrap">
            <Button variant="secondary" onClick={onClose}>Fermer</Button>
            <Button onClick={() => void retry()}>Réessayer</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/45 flex items-end justify-center z-50 pt-[env(safe-area-inset-top)] min-[620px]:items-center">
      <div
        className="bg-card w-full max-w-[520px] max-h-[92dvh] overflow-y-auto rounded-t-[20px] pt-5 px-[18px] pb-[calc(20px+env(safe-area-inset-bottom))] flex flex-col gap-[14px] min-[620px]:rounded-[20px]"
        role="dialog"
        aria-modal="true"
        aria-label="Importer un examen"
      >
        <div className="flex items-center justify-between">
          <h2 className="m-0 text-[18px] font-extrabold">Importer un examen</h2>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Fermer">
            <X size={18} weight="regular" />
          </Button>
        </div>

        {error && <Alert>{error}</Alert>}

        <div className="flex gap-2">
          <button type="button" className={modeClass(mode === 'new')} onClick={() => setMode('new')}>
            Nouvel examen
          </button>
          <button type="button" className={modeClass(mode === 'answer_key')} onClick={() => setMode('answer_key')}>
            Corrigé d'un examen existant
          </button>
        </div>

        {mode === 'new' ? (
          <>
            <label className="flex flex-col gap-[6px] text-[13px] font-bold text-muted-foreground">
              Classe (niveau et matière du sujet)
              <NativeSelect value={classroomId} onChange={(e) => setClassroomId(e.target.value)}>
                <option value="" style={optionStyle}>— choisir une classe —</option>
                {classrooms.map((c) => (
                  <option key={c.id} value={c.id} style={optionStyle}>
                    {c.name}
                    {c.subjects && c.subjects.length ? ` (${c.subjects.map((s) => s.label).filter(Boolean).join(', ')})` : ''}
                  </option>
                ))}
              </NativeSelect>
            </label>

            {fileRow('Sujet — PDF ou photo', subject, subjectRef, pick(setSubject), true)}
            {fileRow('Corrigé — PDF ou photo', answerKey, answerKeyRef, pick(setAnswerKey), false)}

            <label className="flex flex-col gap-[6px] text-[13px] font-bold text-muted-foreground">
              Consigne (optionnel)
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex. : barème sur 20, 1 h" />
            </label>

            <label className="flex items-center gap-2 text-[13px] font-bold text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={hasVariants}
                onChange={(e) => setHasVariants(e.target.checked)}
                className="size-4"
              />
              Le sujet a des variantes ?
            </label>

            {hasVariants && (
              <div className="flex flex-col gap-[8px] pl-1">
                <p className="m-0 text-[12px] text-[var(--naia-text-secondary)]">
                  Donnez un nom à chaque variante — ce nom vous appartient, jamais deviné.
                </p>
                {variantLabels.map((label, i) => (
                  <Input
                    key={i}
                    value={label}
                    placeholder={i === 0 ? 'Ex. : Sujet bleu (référence)' : `Ex. : Variante ${i + 1}`}
                    onChange={(e) => {
                      const next = [...variantLabels];
                      next[i] = e.target.value;
                      setVariantLabels(next);
                    }}
                  />
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="self-start"
                  onClick={() => setVariantLabels((v) => [...v, ''])}
                >
                  <Plus size={14} weight="bold" /> Ajouter une variante
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <label className="flex flex-col gap-[6px] text-[13px] font-bold text-muted-foreground">
              Examen à compléter
              <NativeSelect value={documentId} onChange={(e) => setDocumentId(e.target.value)}>
                <option value="">— choisir une évaluation —</option>
                {exams.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title}
                  </option>
                ))}
              </NativeSelect>
            </label>

            {fileRow('Corrigé — PDF ou photo', answerKey, answerKeyRef, pick(setAnswerKey), true)}

            <p className="m-0 text-[12.5px] leading-snug text-[var(--naia-text-secondary)]">
              Le corrigé complète les questions déjà présentes : les énoncés et le barème existants ne sont pas
              remplacés.
            </p>
          </>
        )}

        <div className="flex gap-[10px] justify-end flex-wrap">
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button disabled={!canSubmit} onClick={() => void submit()}>
            Importer
          </Button>
        </div>

        <p className="m-0 flex items-center gap-2 text-[12px] text-[var(--naia-text-secondary)]">
          <Images size={14} weight="fill" />
          Formats acceptés : PDF ou photo (JPG, PNG, HEIC, WebP).
        </p>
      </div>
    </div>
  );
}
